import { motion } from "framer-motion";

export default function EtTerms() {
  // First Created - 22 Nov 2025
  // Author - Atul Pandey
  // Purpose - Terms of Service page for Eternity / Trinity SaaS platform

  return (
    <section
      id="terms"
      className="min-h-screen bg-black text-gray-200 py-20 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-4xl mx-auto">
        {/* Page Heading */}
        <motion.header
          className="mb-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-widest text-orange-400 mb-2">
            Legal
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-orange-500 mb-3">
            Terms of Service
          </h1>
          <p className="text-sm text-gray-400">
            Effective date:{" "}
            <span className="font-medium text-gray-300">[To be set]</span>
          </p>
        </motion.header>

        <div className="bg-white/5 border border-white/10 rounded-2xl shadow-xl p-6 sm:p-8 space-y-8 text-sm md:text-base leading-relaxed">
          {/* 1. Acceptance */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              1. Acceptance of Terms
            </h2>
            <p className="text-gray-300">
              These Terms of Service (&quot;Terms&quot;) govern your access to and
              use of the websites, applications, and services provided by{" "}
              <span className="font-semibold">
                Eternity Solutions Corp. (&quot;Eternity&quot;, &quot;we&quot;,
                &quot;our&quot;, &quot;us&quot;)
              </span>{" "}
              including the Trinity platform (collectively, the &quot;Services&quot;).
              By accessing or using the Services, you agree to be bound by these
              Terms.
            </p>
          </section>

          {/* 2. Service Description */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              2. Description of the Service
            </h2>
            <p className="text-gray-300">
              Trinity is an AI-enabled SaaS platform that helps leaders capture
              their journey, generate insights, and execute on key priorities
              through structured workflows and guided prompts. We may add, modify,
              or remove features from time to time.
            </p>
          </section>

          {/* 3. Eligibility */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              3. Eligibility
            </h2>
            <p className="text-gray-300">
              You must be at least 18 years old to use the Services. If you are
              using the Services on behalf of an organization, you represent and
              warrant that you have the authority to bind that organization to
              these Terms.
            </p>
          </section>

          {/* 4. Accounts */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              4. Accounts and Security
            </h2>
            <p className="text-gray-300 mb-2">
              To use certain features of the Services, you may need to create an
              account. You agree to:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-300">
              <li>Provide accurate, current, and complete information.</li>
              <li>
                Maintain the security of your login credentials and not share them
                with others.
              </li>
              <li>
                Notify us promptly if you suspect any unauthorized access to or
                use of your account.
              </li>
            </ul>
            <p className="text-gray-300 mt-2">
              You are responsible for all activities that occur under your
              account.
            </p>
          </section>

          {/* 5. License & Acceptable Use */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              5. License and Acceptable Use
            </h2>
            <p className="text-gray-300 mb-2">
              Subject to these Terms, we grant you a limited, non-exclusive,
              non-transferable, revocable license to access and use the Services
              for your internal business purposes.
            </p>
            <p className="text-gray-300 mb-2">
              You agree not to:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-300">
              <li>
                Copy, modify, or create derivative works of the Services or any
                part of them.
              </li>
              <li>
                Reverse engineer, decompile, or attempt to extract the source code
                of the Services.
              </li>
              <li>
                Use the Services for any unlawful purpose or in violation of any
                applicable law or regulation.
              </li>
              <li>
                Access the Services in a way that could damage, disable, or impair
                our infrastructure or interfere with other users.
              </li>
              <li>
                Attempt to gain unauthorized access to any part of the Services or
                related systems or networks.
              </li>
            </ul>
          </section>

          {/* 6. User Content */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              6. User Content
            </h2>
            <p className="text-gray-300 mb-2">
              You may submit content to the Services, including prompts, notes,
              plans, reflections, and other information (&quot;User Content&quot;).
              You retain ownership of your User Content.
            </p>
            <p className="text-gray-300">
              By using the Services, you grant Eternity a limited license to host,
              store, process, and display your User Content solely for the purpose
              of providing and improving the Services, including AI-generated
              insights and recommendations. We do not grant third-party AI
              providers permission to use your User Content for training their
              general models.
            </p>
          </section>

          {/* 7. AI Output */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              7. AI-Generated Content and Disclaimers
            </h2>
            <p className="text-gray-300">
              Trinity uses AI to generate suggestions, summaries, and other forms
              of content based on your inputs. AI-generated content may sometimes
              be incomplete, inaccurate, or not applicable to your specific
              situation. You are responsible for reviewing and validating any
              output, and for decisions made based on such content. Eternity does
              not provide legal, financial, or professional advice through AI
              outputs.
            </p>
          </section>

          {/* 8. Subscriptions & Payment (placeholder) */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              8. Subscriptions and Payment
            </h2>
            <p className="text-gray-300">
              Access to certain features of the Services may require a paid
              subscription or separate agreement. Details regarding pricing,
              billing cycles, and cancellation terms will be provided at the time
              of purchase or in your organization&apos;s agreement with Eternity.
              Unless otherwise stated, fees are non-refundable except where
              required by law.
            </p>
          </section>

          {/* 9. IP */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              9. Intellectual Property
            </h2>
            <p className="text-gray-300">
              The Services, including all software, designs, text, graphics,
              logos, and other materials (excluding User Content), are owned by
              Eternity or its licensors and are protected by intellectual property
              laws. Except as expressly permitted in these Terms, you may not use
              any of our trademarks, branding, or other proprietary content
              without our prior written consent.
            </p>
          </section>

          {/* 10. Third-Party Services */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              10. Third-Party Services and Links
            </h2>
            <p className="text-gray-300">
              The Services may integrate with third-party tools or contain links
              to third-party websites and services. Your use of those services is
              governed by the terms and policies of the respective third parties.
              We are not responsible for the content, policies, or practices of
              any third-party services.
            </p>
          </section>

          {/* 11. Termination */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              11. Termination
            </h2>
            <p className="text-gray-300">
              We may suspend or terminate your access to the Services if you
              violate these Terms, use the Services in a way that could cause
              harm, or where required by law. You may stop using the Services at
              any time. Certain provisions of these Terms, including those
              relating to intellectual property, disclaimers, and limitations of
              liability, will survive termination.
            </p>
          </section>

          {/* 12. Limitation of Liability */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              12. Limitation of Liability
            </h2>
            <p className="text-gray-300">
              To the maximum extent permitted by law, Eternity and its officers,
              directors, employees, and affiliates will not be liable for any
              indirect, incidental, consequential, special, or exemplary damages,
              or for any loss of profits, revenues, data, or goodwill, arising
              out of or in connection with your use of the Services. Our total
              aggregate liability for any claim relating to the Services will be
              limited to the amount you paid to us for the Services in the
              twelve (12) months preceding the event giving rise to the claim.
            </p>
          </section>

          {/* 13. Indemnity */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              13. Indemnification
            </h2>
            <p className="text-gray-300">
              You agree to indemnify and hold harmless Eternity, its officers,
              directors, employees, and affiliates from and against any claims,
              liabilities, damages, losses, and expenses (including reasonable
              legal fees) arising out of or in connection with your use of the
              Services, your User Content, or your violation of these Terms.
            </p>
          </section>

          {/* 14. Governing Law */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              14. Governing Law and Dispute Resolution
            </h2>
            <p className="text-gray-300">
              These Terms are governed by the laws of the State of California
              (without regard to its conflict of laws principles), unless
              otherwise specified in a separate written agreement. Any disputes
              arising out of or relating to these Terms or the Services will be
              resolved in the courts located in California, subject to any
              mandatory arbitration or dispute resolution provisions agreed in
              writing.
            </p>
          </section>

          {/* 15. Changes */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              15. Changes to These Terms
            </h2>
            <p className="text-gray-300">
              We may update these Terms from time to time. When we do, we will
              revise the &quot;Effective date&quot; above and may provide
              additional notice where appropriate. Your continued use of the
              Services after the updated Terms become effective constitutes your
              acceptance of the changes.
            </p>
          </section>

          {/* 16. Contact */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              16. Contact Us
            </h2>
            <p className="text-gray-300">
              If you have questions about these Terms or the Services, please
              contact us at:
            </p>
            <p className="mt-2 text-gray-200">
              <span className="block font-semibold">
                Eternity Solutions Corp.
              </span>
              <span className="block">Belmont, CA, USA</span>
              <a
                href="mailto:trinity@eternity-solutions.com"
                className="block text-orange-400 hover:text-orange-300 mt-1"
              >
                trinity@eternity-solutions.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </section>
  );
}